<script setup>
import { computed } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { Badge } from '@drama/ui'
import { ExternalLink } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import { stepExecutionDetail } from './run-video-match-step-detail.js'

const props = defineProps({
  data: { type: Object, required: true },
  selected: { type: Boolean, default: false },
})

const { t, te } = useI18n()
const turn = computed(() => props.data.turn)
const step = computed(() => props.data.kind === 'step' ? props.data.step : null)
const executionDetail = computed(() => stepExecutionDetail({
  step: step.value,
  stepIndex: props.data.step_index,
  turn: turn.value,
  t,
}))
const nodeTone = computed(() => props.data.kind === 'turn'
  ? 'is-turn'
  : `is-${statusTone(step.value.status)}`)
const targetPosition = computed(() => props.data.flow_direction === 'right' ? Position.Left : Position.Right)
const sourcePosition = computed(() => props.data.flow_direction === 'right' ? Position.Right : Position.Left)

function statusVariant(status) {
  switch (status) {
    case 'passed':
    case 'ok':
    case 'available':
    case 'selected':
      return 'success'
    case 'error':
      return 'danger'
    case 'rejected':
    case 'needs_llm_selection':
    case 'stopped':
      return 'warning'
    case 'empty':
    case 'below_minimum':
    case 'miss':
    case 'not_recorded':
    case 'uncovered':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function methodLabel(method) {
  switch (method) {
    case 'exact_transition':
    case 'semantic_reuse':
    case 'intent_video':
      return t(`visualGameAdmin.llm.videoMatch.methods.${method}`)
    case null:
      return t('visualGameAdmin.llm.videoMatch.methods.none')
    default:
      return method
  }
}

function statusTone(status) {
  switch (status) {
    case 'passed':
    case 'ok':
    case 'available':
    case 'selected':
      return 'success'
    case 'error':
    case 'rejected':
    case 'needs_llm_selection':
    case 'stopped':
      return 'warning'
    case 'empty':
    case 'below_minimum':
    case 'miss':
    case 'not_recorded':
    case 'uncovered':
      return 'muted'
    default:
      return 'muted'
  }
}

function stepLabel(kind) {
  const key = `visualGameAdmin.llm.videoMatch.steps.${kind}`
  return te(key) ? t(key) : kind
}

function statusLabel(status) {
  const key = `visualGameAdmin.llm.videoMatch.status.${status}`
  return te(key) ? t(key) : status
}

function hasModelDecisionCall(stepValue) {
  return (stepValue.kind === 'semantic_rerank' || stepValue.kind === 'intent_llm_selection')
    && Object.hasOwn(stepValue, 'call_id')
}
</script>

<template>
  <article class="run-match-node" :class="[{ 'is-selected': selected }, nodeTone]">
    <Handle type="target" :position="targetPosition" />

    <template v-if="data.kind === 'turn'">
      <span class="run-match-node__eyebrow">
        {{ t('visualGameAdmin.llm.runVideoMatch.turnNumber', { number: turn.context.turn_no }) }}
      </span>
      <h3>{{ t('visualGameAdmin.llm.runVideoMatch.playerInput') }}</h3>
      <p>{{ turn.context.input }}</p>
      <footer>
        <span>{{ methodLabel(turn.context.match_method) }}</span>
        <strong v-if="turn.context.match_similarity !== null">{{ turn.context.match_similarity.toFixed(4) }}</strong>
      </footer>
    </template>

    <template v-else>
      <header>
        <span class="run-match-node__eyebrow">
          {{ t('visualGameAdmin.llm.videoMatch.stepNumber', { number: data.step_index + 1 }) }}
        </span>
        <Badge :variant="statusVariant(step.status)" size="sm">
          {{ statusLabel(step.status) }}
        </Badge>
      </header>
      <h3>{{ stepLabel(step.kind) }}</h3>
      <p>{{ executionDetail.summary }}</p>
      <RouterLink
        v-if="hasModelDecisionCall(step)"
        class="run-match-node__call-link nodrag nopan"
        :to="{ name: 'llm-calls', query: { capability: 'text', request_id: step.call_id } }"
        target="_blank"
        rel="noopener noreferrer"
        @click.stop
      >
        {{ t('visualGameAdmin.llm.runVideoMatch.openModelInputOutput') }}
        <ExternalLink :size="13" />
      </RouterLink>
    </template>

    <Handle type="source" :position="sourcePosition" />
  </article>
</template>

<style scoped>
.run-match-node {
  display: grid;
  align-content: start;
  gap: var(--drama-space-xs);
  width: 220px;
  min-height: 112px;
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-top-width: 3px;
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  cursor: pointer;
}

.run-match-node:hover,
.run-match-node.is-selected {
  border-color: var(--drama-color-primary);
}

.run-match-node.is-selected {
  box-shadow: var(--drama-shadow-sm);
}

.run-match-node.is-turn {
  border-top-color: var(--drama-color-primary);
  background: color-mix(in srgb, var(--drama-color-primary) 5%, var(--drama-color-surface));
}

.run-match-node.is-success {
  border-top-color: var(--drama-color-success);
}

.run-match-node.is-warning {
  border-top-color: var(--drama-color-warning);
}

.run-match-node.is-muted {
  border-top-color: var(--drama-color-border-strong);
}

.run-match-node header,
.run-match-node footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
}

.run-match-node h3,
.run-match-node p {
  margin: 0;
}

.run-match-node h3 {
  font-size: var(--drama-font-size-sm);
  line-height: 1.4;
}

.run-match-node p {
  display: -webkit-box;
  overflow: hidden;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.run-match-node footer {
  margin-top: var(--drama-space-xs);
  padding-top: var(--drama-space-xs);
  border-top: 1px solid var(--drama-color-border);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.run-match-node footer strong {
  color: var(--drama-color-success);
}

.run-match-node__eyebrow {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.run-match-node__code {
  font-family: var(--drama-font-family-mono);
  overflow-wrap: anywhere;
}

.run-match-node__call-link {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  width: fit-content;
  margin-top: var(--drama-space-xs);
  color: var(--drama-color-primary);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-semibold);
  text-decoration: none;
}

.run-match-node__call-link:hover {
  text-decoration: underline;
}

.run-match-node :deep(.vue-flow__handle) {
  opacity: 0;
}
</style>
